import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PENDING_BILLING_MODAL_STORAGE_KEY, useAuth } from "../contexts/AuthContext";
import { signOut, getOrganization } from "../services/authService";
import "./DashboardLayout.css";

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [fundedByUniversity, setFundedByUniversity] = useState(false);
  const [billingExempt, setBillingExempt] = useState(false);
  const [billingUnlocked, setBillingUnlocked] = useState(false);
  const [showBillingSetupModal, setShowBillingSetupModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isSyllySyncAdmin, isOrgAdminUser, isTeamMember, isOrgMember, orgDomain, accessibleOrgs, switchOrganization } = useAuth();

  useEffect(() => {
    const loadOrgMeta = async () => {
      if (!orgDomain || !(isOrgAdminUser || isTeamMember || isOrgMember)) {
        setFundedByUniversity(false);
        setBillingExempt(false);
        setBillingUnlocked(false);
        return;
      }

      try {
        const org = await getOrganization(orgDomain);
        if (!org) {
          setFundedByUniversity(false);
          setBillingExempt(false);
          setBillingUnlocked(false);
          return;
        }

        const isFunded = !!org.fundedByUniversity;
        const isExempt = !!org.billingExempt;
        const hasBilling = !!(org.stripe_customer_id && org.stripe_subscription_id);
        const isUnlocked = isFunded || isExempt || hasBilling;
        setFundedByUniversity(isFunded);
        setBillingExempt(isExempt);
        setBillingUnlocked(isUnlocked);

        if (isUnlocked) {
          setShowBillingSetupModal(false);
          return;
        }

        if (isOrgAdminUser) {
          try {
            const pendingBillingOrg = localStorage.getItem(PENDING_BILLING_MODAL_STORAGE_KEY);
            if (pendingBillingOrg === orgDomain) {
              setShowBillingSetupModal(true);
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (err) {
        console.error('Error loading org:', err);
      }
    };
    loadOrgMeta();
  }, [orgDomain, isOrgAdminUser, isTeamMember, isOrgMember]);

  const dismissBillingSetupModal = () => {
    try {
      localStorage.removeItem(PENDING_BILLING_MODAL_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    setShowBillingSetupModal(false);
  };

  const goToBillingSetup = () => {
    dismissBillingSetupModal();
    navigate("/org-dashboard/billing");
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSwitchOrg = async (orgId) => {
    if (orgId === orgDomain) return;
    try {
      await switchOrganization(orgId);
      navigate("/org-dashboard/signups");
    } catch (err) {
      console.error("Error switching organization:", err);
    }
  };

  // Different menu items based on user type
  const getMenuItems = () => {
    if (isSyllySyncAdmin) {
      return [
        { id: "signups", label: "Signups", path: "/dashboard/signups", icon: "📊" },
        { id: "revenue", label: "Revenue", path: "/dashboard/revenue", icon: "💰" },
        { id: "schools", label: "Schools", path: "/dashboard/schools", icon: "🏫" },
        { id: "courses", label: "Courses", path: "/dashboard/courses", icon: "📚" },
        { id: "organizations", label: "Organizations", path: "/dashboard/organizations", icon: "🏢" },
        { id: "creators", label: "Creators", path: "/dashboard/creators", icon: "⭐" },
      ];
    } else if (isOrgAdminUser || isTeamMember) {
      const baseTabs = [
        { id: "grades", label: "Grades", path: "/org-dashboard/grades", icon: "🎓" },
        { id: "study-hours", label: "Study Hours", path: "/org-dashboard/study-hours", icon: "⏱️" },
        { id: "study-locations", label: "Study Locations", path: "/org-dashboard/study-locations", icon: "📍" },
        ...(billingUnlocked ? [
          { id: "member-requests", label: "Member Requests", path: "/org-dashboard/member-requests", icon: "📝" },
          { id: "members", label: "Members", path: "/org-dashboard/members", icon: "👥" },
        ] : []),
      ];
      
      if (isOrgAdminUser) {
        const adminTabs = [
          ...baseTabs,
          ...(billingUnlocked ? [{ id: "team", label: "Team", path: "/org-dashboard/team", icon: "👤" }] : []),
        ];
        if (!fundedByUniversity && !billingExempt) {
          adminTabs.push({ id: "billing", label: "Billing", path: "/org-dashboard/billing", icon: "💳" });
        }
        return adminTabs;
      }
      
      return baseTabs;
    } else if (isOrgMember) {
      return [
        { id: "signups", label: "Signups", path: "/org-dashboard/signups", icon: "📊" },
      ];
    } else {
      return [];
    }
  };

  const menuItems = getMenuItems();

  return (
    <div className="dashboard-container">
      <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          {sidebarOpen && (
            <img src="/textLogo.png" alt="SyllySync" className="sidebar-logo" />
          )}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? "←" : "→"}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.length > 0 ? (
            menuItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${
                  location.pathname === item.path ? "active" : ""
                }`}
                onClick={() => navigate(item.path)}
              >
                <span className="nav-icon">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </button>
            ))
          ) : (
            <div className="no-menu-items">
              {sidebarOpen && <p>No menu items available</p>}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && accessibleOrgs && accessibleOrgs.length > 1 && (
            <div className="org-switcher">
              <div className="org-switcher-label">Organization</div>
              <select
                className="org-switcher-select"
                value={orgDomain || ""}
                onChange={(e) => handleSwitchOrg(e.target.value)}
              >
                {accessibleOrgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="user-info">
            {sidebarOpen && (
              <>
                <div className="user-email">{currentUser?.email}</div>
                <div className="user-badge">
                  {isSyllySyncAdmin || isOrgAdminUser ? 'Admin' : isTeamMember ? 'Team Member' : 'Member'}
                </div>
              </>
            )}
          </div>
          <button className="sign-out-button" onClick={handleSignOut}>
            <span className="nav-icon">🚪</span>
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <main
        className={`main-content ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <div className="content-wrapper">{children}</div>
      </main>

      {showBillingSetupModal && (
        <div className="billing-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="billing-modal-title">
          <div className="billing-modal">
            <div className="billing-modal-icon">💳</div>
            <h2 id="billing-modal-title">Set up billing to activate your organization</h2>
            <p>
              Your organization has been created, but it will not become active until billing
              is set up under the Billing tab.
            </p>
            <div className="billing-modal-actions">
              <button type="button" className="billing-modal-secondary" onClick={dismissBillingSetupModal}>
                I’ll do this later
              </button>
              <button type="button" className="billing-modal-primary" onClick={goToBillingSetup}>
                Go to Billing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
